/**
 * Share (or download) a plant's Care Log growth timeline as a PNG image
 * collage — the visual companion to the PDF export.
 *
 * Pipeline mirrors `shareLibrary.js`:
 *   1. Resolve every care-log photo to a data URL (upfront) so html2canvas
 *      does not have to fight CORS mid-render.
 *   2. Build an off-screen host with a title strip + grid of dated photo
 *      cells + optional notes.
 *   3. Snapshot via html2canvas → PNG blob.
 *   4. Prefer `navigator.share({files:[...]})` for one-tap native share.
 *   5. Fall back to a download link when the browser can't share files.
 *
 * All state is transient — the off-screen host is removed in a `finally`
 * block so we never leak DOM on error.
 */

const MAX_CELLS = 12; // Keep the image sharable (Twitter/WhatsApp limits)

/**
 * Compact HTML escape — same helper used by pdfExport and shareLibrary.
 * Kept inline instead of a shared util because it's tiny and the alternative
 * would introduce an unnecessary cross-file dependency for one function.
 */
function escapeHtml(s) {
  return (s == null ? '' : String(s))
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function _buildCollageHost({ plantName, scientificName, cells }) {
  const host = document.createElement('div');
  host.style.cssText = [
    'position:absolute', 'left:-99999px', 'top:0',
    'width:720px', 'padding:24px',
    'background:linear-gradient(135deg,#f0f7f0,#e5f0e5)',
    'font-family: system-ui, -apple-system, "Segoe UI", "Noto Sans", "Noto Sans CJK JP", "Noto Sans CJK SC", "Noto Sans CJK KR", "Hiragino Sans", "Yu Gothic", "Microsoft YaHei", "Malgun Gothic", sans-serif',
    'color:#2d2d2d', 'font-size:12px', 'line-height:1.5',
  ].join(';');
  const cellsHtml = cells.map((e) => {
    const dateStr = new Date(e.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return `
      <div style="width:calc(33.33% - 11px);margin-bottom:16px;box-sizing:border-box;">
        <div style="width:100%;padding-top:100%;position:relative;background:#f2f5f2;border-radius:8px;overflow:hidden;box-shadow:0 2px 6px rgba(0,0,0,0.08);">
          <img src="${escapeHtml(e._src)}" style="position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;" />
        </div>
        <div style="margin-top:6px;text-align:center;">
          <div style="font-weight:600;color:#2d523c;text-transform:capitalize;font-size:11px;">${escapeHtml(e.action)}</div>
          <div style="color:#6e6e6e;font-size:10px;">${escapeHtml(dateStr)}</div>
          ${e.note ? `<div style="color:#5a5a5a;font-size:10px;font-style:italic;margin-top:2px;overflow-wrap:anywhere;">${escapeHtml(e.note)}</div>` : ''}
        </div>
      </div>`;
  }).join('');
  host.innerHTML = `
    <div style="text-align:center;margin-bottom:16px;">
      <h1 style="font-size:22px;color:#2d523c;margin:0 0 2px 0;">${escapeHtml(plantName || 'Plant')} — Growth timeline</h1>
      ${scientificName ? `<div style="font-style:italic;color:#6e6e6e;font-size:12px;margin-bottom:6px;">${escapeHtml(scientificName)}</div>` : ''}
      <div style="color:#5a5a5a;font-size:11px;">
        ${cells.length} photo${cells.length !== 1 ? 's' : ''} · personalplants.online
      </div>
    </div>
    <div style="display:flex;flex-wrap:wrap;gap:16px;justify-content:flex-start;">${cellsHtml}</div>
  `;
  document.body.appendChild(host);
  return host;
}

/**
 * Signature mirrors `exportCareTimelineToPDF`:
 *   shareCareTimeline({ plant, entries, resolvePhotoSrc })
 * Returns `{ shared: boolean, downloaded: boolean }`.
 */
export async function shareCareTimeline({ plant, entries, resolvePhotoSrc }) {
  if (!entries || entries.length === 0) {
    throw new Error('No timeline photos to share');
  }
  // Cap at MAX_CELLS to keep the image size sane. Take the most recent by
  // date so a plant with many entries surfaces the freshest growth.
  const capped = entries.length > MAX_CELLS
    ? [...entries].sort((a, b) => (b.at || '').localeCompare(a.at || '')).slice(0, MAX_CELLS)
    : entries;

  // Resolve every photo upfront to a data URL.
  const resolved = await Promise.all(
    capped.map(async (e) => {
      try {
        const src = await resolvePhotoSrc(e);
        return { ...e, _src: src };
      } catch (_err) {
        return { ...e, _src: null };
      }
    })
  );
  const usable = resolved
    .filter((r) => r._src)
    // Chronological ordering so the collage reads like a time-lapse.
    .sort((a, b) => (a.at || '').localeCompare(b.at || ''));
  if (usable.length === 0) throw new Error('No timeline photos could be loaded');

  const { default: html2canvas } = await import('html2canvas');
  const host = _buildCollageHost({
    plantName: plant?.common_name,
    scientificName: plant?.scientific_name,
    cells: usable,
  });

  try {
    // Wait for every <img> to actually paint before snapshotting so
    // html2canvas doesn't capture a blank frame.
    await new Promise((r) => setTimeout(r, 100));
    await Promise.all(Array.from(host.querySelectorAll('img')).map((img) => {
      if (img.complete) return null;
      return new Promise((r) => { img.onload = r; img.onerror = r; });
    }));

    const canvas = await html2canvas(host, {
      scale: 2, backgroundColor: null, useCORS: true, logging: false,
    });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.95));
    if (!blob) throw new Error('Could not render image');

    const slug = (plant?.common_name || 'plant').toLowerCase()
      .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'plant';
    const filename = `${slug}_timeline_${new Date().toISOString().split('T')[0]}.png`;
    const file = new File([blob], filename, { type: 'image/png' });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `${plant?.common_name || 'Plant'} — Growth timeline`,
          text: `Watch my plant grow 🌿 · personalplants.online`,
        });
        return { shared: true, downloaded: false };
      } catch (err) {
        // User cancelled — no-op, do NOT fall through to a download.
        if (err && err.name === 'AbortError') {
          return { shared: false, downloaded: false };
        }
        // Any other share error → fall through to download.
      }
    }

    // Fallback: trigger a download link.
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return { shared: false, downloaded: true };
  } finally {
    host.remove();
  }
}
